import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { login, logout } from "@/redux/slices/auth";
import api from "@/service/api";
import { loginWithGoogle, loginWithUserAndPassword } from "@/service/user";
import { useGoogleLogin } from "@react-oauth/google";
import { Notyf } from "notyf";
import { useCallback, useEffect, useState } from "react";
import { format } from "@react-input/mask";
import * as yup from "yup";
import { cpf } from "cpf-cnpj-validator";
import { cn, emailValidationRegex } from "@/utils";
import PhoneBrValidator from "@/utils/phoneValidator";
import Button from "@/components/button";
import Field from "@/components/field";

export type userType =
  | "EMAIL_PASSWORD_USER"
  | "NEW_USER"
  | "GOOGLE_USER"
  | "USER_FROM_DASHBOARD";

export interface UserData {
  type: userType | null;
  name: string;
  cpf: string;
  email: string;
  phone: string;
}

export interface UserLoginData {
  password: string;
  email: string;
}

interface UserProps {
  userData: UserData | null;
  submitData: (data: UserData) => void;
}

const userSchema = yup.object().shape({
  name: yup.string().required("Informe o nome"),
  email: yup
    .string()
    .required("Digite o e-mail")
    .matches(emailValidationRegex, "E-mail inválido"),
  cpf: yup
    .string()
    .required("Digite seu CPF")
    .test("validate-cpf", "CPF inválido", (value) => {
      if (value) {
        return cpf.isValid(value);
      }
      return false;
    }),
  phone: yup
    .string()
    .required("Digite o número do celular")
    .test("validate-phone", "Informe um número válido", (value) => {
      if (value) {
        return PhoneBrValidator.isPhone(value);
      }
    }),
});

const loginSchema = yup.object({
  email: yup
    .string()
    .required("Digite o e-mail")
    .matches(emailValidationRegex, "E-mail inválido"),
  password: yup.string().required("Digite a senha"),
});

export default function User(props: UserProps) {
  const dispatch = useAppDispatch();
  const auth = useAppSelector((state) => state.auth.auth);

  const [loadingLogin, setLoadingLogin] = useState(false);
  const [name, setName] = useState(props.userData?.name || "");
  const [cpf, setCpf] = useState(props.userData?.cpf || "");
  const [email, setEmail] = useState(props.userData?.email || "");
  const [phone, setPhone] = useState(props.userData?.phone || "");
  const [emailUser, setEmailUser] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [userType, setUserType] = useState<userType | null>(
    props.userData?.type || "EMAIL_PASSWORD_USER"
  );
  const [errors, setErrors] = useState<Record<string, string[]> | null>(null);
  const [errorsLogin, setErrorsLogin] = useState<Record<
    string,
    string[]
  > | null>(null);

  const handleLoginWithGoogle = useGoogleLogin({
    onSuccess: (tokenReponse) => {
      const { access_token: accessToken } = tokenReponse;

      api
        .get(
          `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${accessToken}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              Accept: "application/json",
            },
          }
        )
        .then((res) => {
          const { data } = res;

          loginWithGoogle(data.id, "google")
            .then((userData) => {
              if (userData.Token) {
                dispatch(login(userData));
              }
            })
            .catch(() => {
              const notyf = new Notyf({
                duration: 3000,
                position: { x: "center", y: "bottom" },
              });
              notyf.error("Usuário não encontrado !");
            });
        })
        .catch(() => {
          const notyf = new Notyf({
            duration: 3000,
            position: { x: "center", y: "bottom" },
          });
          notyf.error("Ocorreu um erro ao buscar informações do usuário");
        });
    },
    onError: () => {
      const notyf = new Notyf({
        duration: 3000,
        position: { x: "center", y: "bottom" },
      });
      notyf.error(
        "Ocorreu um erro ao finalizar o cadastro com sua conta google"
      );
      setUserType(null);
    },
    onNonOAuthError: () => {
      setUserType(null);
    },
  });

  const logoutUser = () => {
    dispatch(logout());
    setName("");
    setCpf("");
    setEmail("");
    setPhone("");
    setEmailUser("");
    setPassword("");
  };

  const validateForm = async () => {
    const data: UserData = {
      type: userType,
      name: name,
      cpf: cpf,
      email: email,
      phone: phone,
    };
    try {
      await userSchema.validate(data, { abortEarly: false });
      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const nextErrors: Record<string, string[]> = {};
        err.inner.forEach((element) => {
          const path = element.path || "root";
          if (nextErrors[path]) {
            nextErrors[path].push(element?.message || "");
          } else {
            nextErrors[path] = [element?.message || ""];
          }
        });
        setErrors(nextErrors);
        return false;
      }
    }
  };

  const validateFormLogin = async () => {
    const data: UserLoginData = {
      email: emailUser,
      password: password,
    };

    try {
      await loginSchema.validate(data, { abortEarly: false });
      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const nextErrors: Record<string, string[]> = {};
        err.inner.forEach((element) => {
          const path = element.path || "root";
          if (nextErrors[path]) {
            nextErrors[path].push(element?.message || "");
          } else {
            nextErrors[path] = [element?.message || ""];
          }
        });
        setErrorsLogin(nextErrors);
        return false;
      }
    }
  };

  const validateField = async (
    schema: yup.Schema,
    fieldName: string,
    value: string,
    currentErrors: Record<string, string[]> | null,
    setCurrentErrors: (errors: Record<string, string[]> | null) => void
  ) => {
    try {
      await schema.validateAt(fieldName, { [fieldName]: value });
      const allErrors = { ...currentErrors };
      delete allErrors[fieldName];
      setCurrentErrors(allErrors);
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const allErrors = { ...currentErrors };
        allErrors[fieldName] = err.errors;
        setCurrentErrors(allErrors);
        return false;
      }
    }
  };

  const loginEmailPassword = useCallback(async () => {
    if (await validateFormLogin()) {
      setLoadingLogin(true);
      await loginWithUserAndPassword(emailUser, password)
        .then((userData) => {
          if (userData.Token) {
            dispatch(login(userData));
          }
        })
        .finally(() => {
          setLoadingLogin(false);
        })
        .catch(() => {
          const notyf = new Notyf({
            duration: 3000,
            position: { x: "center", y: "bottom" },
          });
          notyf.error("Usuário não encontrado !");
        });
    }
  }, [emailUser, password, dispatch]);

  const submitData = async () => {
    const data: UserData = {
      type: userType,
      name: name,
      cpf: cpf,
      email: email,
      phone: phone,
    };

    if (await validateForm()) {
      props.submitData(data);
    }
  };

  const isError = useCallback(
    (field: string) => (errors && errors[field] && errors[field][0]) || null,
    [errors]
  );

  const isErrorLogin = useCallback(
    (field: string) =>
      (errorsLogin && errorsLogin[field] && errorsLogin[field][0]) || null,
    [errorsLogin]
  );

  const clearErrors = () => {
    setErrors(null);
    setErrorsLogin(null);
  };

  useEffect(() => {
    if (auth?.Token) {
      setName(auth.user.Name);
      setEmail(auth.user.Email);

      if (auth.user.Cpf) {
        setCpf(
          format(auth.user.Cpf, {
            mask: "___.___.___-__",
            replacement: { _: /\d/ },
          })
        );
      }

      if (auth.user.Phone) {
        setPhone(
          format(auth.user.Phone, {
            mask: "(__) _____-____",
            replacement: { _: /\d/ },
          })
        );
      }

      if (auth.user.PlanId) {
        setUserType("USER_FROM_DASHBOARD");
      }
    }
  }, [auth]);

  const tabClass = (active: boolean) =>
    cn(
      "h-11 rounded-full px-4 text-sm font-medium transition-all duration-500 ease-soft",
      active
        ? "bg-checkout-fg text-checkout-bg"
        : "bg-checkout-fill text-checkout-muted hover:bg-checkout-fill-strong"
    );

  return (
    <div className="flex flex-col gap-8 pb-10">
      <div className="flex flex-wrap gap-2">
        <button
          className={tabClass(
            userType === "EMAIL_PASSWORD_USER" ||
              userType === "USER_FROM_DASHBOARD"
          )}
          onClick={() => {
            setUserType("EMAIL_PASSWORD_USER");
            clearErrors();
          }}
        >
          Continuar com e-mail
        </button>
        <button
          className={tabClass(userType === "NEW_USER")}
          onClick={() => {
            setUserType("NEW_USER");
            logoutUser();
            clearErrors();
          }}
        >
          Não tenho conta
        </button>
        <button
          className={tabClass(userType === "GOOGLE_USER")}
          onClick={() => {
            setUserType("GOOGLE_USER");
            handleLoginWithGoogle();
            clearErrors();
          }}
        >
          Continuar com Google
        </button>
      </div>

      {(userType === "EMAIL_PASSWORD_USER" ||
        userType === "GOOGLE_USER" ||
        userType === "USER_FROM_DASHBOARD") && (
        <>
          {auth?.Token ? (
            <div className="rounded-shell border border-checkout-hairline bg-checkout-surface p-6">
              <p className="text-[11px] uppercase tracking-[0.16em] text-checkout-muted">
                Logado como
              </p>
              <h3 className="mt-2 font-display text-2xl font-semibold">
                {auth.user.Email}
              </h3>
              <Button variant="secondary" className="mt-5" onClick={logoutUser}>
                Deslogar
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <h1 className="font-display text-3xl font-semibold md:text-4xl">
                Informe seu usuário
              </h1>
              <Field
                label="E-mail"
                value={emailUser}
                onChange={(vl) => setEmailUser(vl.target.value)}
                disabled={loadingLogin || !!auth?.Token}
                error={isErrorLogin("email")}
                onBlur={() =>
                  validateField(
                    loginSchema,
                    "email",
                    emailUser,
                    errorsLogin,
                    setErrorsLogin
                  )
                }
              />
              <Field
                label="Senha"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(vl) => setPassword(vl.target.value)}
                disabled={loadingLogin || !!auth?.Token}
                error={isErrorLogin("password")}
                onBlur={() =>
                  validateField(
                    loginSchema,
                    "password",
                    password,
                    errorsLogin,
                    setErrorsLogin
                  )
                }
                icon={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="text-checkout-muted hover:text-checkout-fg"
                    aria-label="Mostrar senha"
                  >
                    <span
                      className={
                        showPassword
                          ? "icon-[tabler--eye-off] size-5"
                          : "icon-[tabler--eye] size-5"
                      }
                    />
                  </button>
                }
              />
              <Button
                className="mt-2 w-full"
                onClick={loginEmailPassword}
                loading={loadingLogin}
                disabled={!!auth?.Token}
              >
                Entrar
              </Button>
            </div>
          )}
        </>
      )}

      {(auth?.Token || userType === "NEW_USER") && (
        <div className="flex flex-col gap-4">
          <h1 className="font-display text-3xl font-semibold md:text-4xl">
            Dados pessoais
          </h1>
          <Field
            label="Nome"
            value={name}
            onChange={(vl) => setName(vl.target.value)}
            disabled={!!auth?.Token && name !== ""}
            error={isError("name")}
            onBlur={() =>
              validateField(userSchema, "name", name, errors, setErrors)
            }
          />
          <Field
            label="E-mail"
            value={email}
            onChange={(vl) => setEmail(vl.target.value)}
            disabled={!!auth?.Token && email !== ""}
            error={isError("email")}
            onBlur={() =>
              validateField(userSchema, "email", email, errors, setErrors)
            }
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="CPF"
              placeholder="xxx.xxx.xxx-xx"
              value={cpf}
              onChange={(vl) => {
                setCpf(
                  format(vl.target.value, {
                    mask: "___.___.___-__",
                    replacement: { _: /\d/ },
                  })
                );
              }}
              disabled={!!auth?.Token && cpf !== ""}
              error={isError("cpf")}
              onBlur={() =>
                validateField(userSchema, "cpf", cpf, errors, setErrors)
              }
            />
            <Field
              label="Celular"
              placeholder="(xx) xxxxx-xxxx"
              value={phone}
              onChange={(vl) => {
                setPhone(
                  format(vl.target.value, {
                    mask: "(__) _____-____",
                    replacement: { _: /\d/ },
                  })
                );
              }}
              disabled={!!auth?.Token && phone !== ""}
              error={isError("phone")}
              onBlur={() =>
                validateField(userSchema, "phone", phone, errors, setErrors)
              }
            />
          </div>
          <Button className="mt-4 w-full" onClick={submitData}>
            Continuar
          </Button>
        </div>
      )}
    </div>
  );
}
